"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";
import { ReactiveImage } from "@/components/ReactiveImage";
import { useLazyVideo } from "@/components/motion/useLazyVideo";
import styles from "./Showreel.module.scss";

/**
 * Hero band under the opener: the selected-work showreel in a large left tile
 * paired with a flux-gradient abstract on the right. The reel tile is locked to
 * 16:9 and the video uses `object-fit: contain`, so the reel is never cropped;
 * the abstract fills the matched height and may crop freely. Muted autoplay
 * loop with a corner pause toggle (WCAG 2.2.2) — clicking anywhere on the
 * reel toggles too; under prefers-reduced-motion it holds on the poster with
 * native controls.
 */
export function Showreel() {
  const reduceMotion = useReducedMotion() ?? false;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [framed, setFramed] = useState(false);

  // The reel's poster is the home LCP, so the file attaches only after the
  // page has loaded (720p on phones); useLazyVideo also carries the iOS
  // muted-autoplay contract. If playback is refused (e.g. Low Power Mode)
  // the poster overlay stays up and the corner toggle starts it on tap.
  useLazyVideo(videoRef, "/videos/showreel.mp4", {
    mode: "afterLoad",
    reduceMotion,
    mobileSrc: "/videos/showreel-mobile.mp4",
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const sync = () => setPlaying(!v.paused);
    /* The poster overlay lifts on the first frame the video actually
       PAINTS, not on `play` (which fires before any frame is decoded). The
       reel loops back into frame 0 and the poster is frame 0, so the
       crossfade is between identical pixels — the reel just starts moving.
       Once lifted it stays down: a paused reel shows its own frame. */
    let frameCb = 0;
    const reveal = () => setFramed(true);
    const onPlaying = () => {
      if ("requestVideoFrameCallback" in v) {
        frameCb = v.requestVideoFrameCallback(reveal);
      } else reveal();
    };
    v.addEventListener("play", sync);
    v.addEventListener("pause", sync);
    v.addEventListener("playing", onPlaying);
    sync();
    return () => {
      v.removeEventListener("play", sync);
      v.removeEventListener("pause", sync);
      v.removeEventListener("playing", onPlaying);
      if (frameCb) v.cancelVideoFrameCallback(frameCb);
    };
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  return (
    <section className={`oi-page ${styles.band}`} aria-label="Showreel">
      <div className={styles.grid}>
        <div className={styles.reel}>
          <video
            ref={videoRef}
            className={styles.video}
            onClick={reduceMotion ? undefined : toggle}
            poster="/images/showreel-poster.webp"
            loop
            muted
            playsInline
            controls={reduceMotion}
            preload="none"
            aria-label="Selected work showreel"
          />
          {!reduceMotion && (
            /* Same URL as the video's poster attribute, so this costs no
               extra download. It guarantees the tile is never black while
               playback hasn't started. */
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className={styles.poster}
              src="/images/showreel-poster.webp"
              alt=""
              aria-hidden="true"
              data-hidden={framed || undefined}
            />
          )}
          {!reduceMotion && (
            <button
              type="button"
              className={styles.toggle}
              onClick={toggle}
              aria-label={playing ? "Pause showreel" : "Play showreel"}
            >
              {/* Both glyphs stay mounted and crossfade with a small turn,
                  so the state change reads as one control morphing. */}
              <svg
                className={styles.glyph}
                data-on={playing || undefined}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
              <svg
                className={styles.glyph}
                data-on={!playing || undefined}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          )}
        </div>
        <div className={styles.aside} aria-hidden="true">
          <ReactiveImage
            src="/images/gallery/abstract-flux-arc.webp"
            alt=""
            sizes="(max-width: 767px) 100vw, 33vw"
            priority
          />
        </div>
      </div>
    </section>
  );
}
